import React, { useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Box, Fab, Typography } from '@mui/material';
import GridItem from '@jumbo/components/GridItem';
import { HOST, UPLOAD_MAX_IMAGE_SIZE } from 'config/config';
import { DropzoneArea } from 'react-mui-dropzone';
import { Delete } from '@mui/icons-material';
import { BlobStorageTypeEnumValue } from 'enums/BlobStorageTypeEnum';
import { ExteriorSignageQuoteAttachedFileTypeEnum } from 'enums/ExteriorSignageQuoteAttachedFileTypeEnum';
import {
  useDeleteExteriorSignageQuoteFile,
  useUpdateExteriorSignageQuoteFile,
} from 'services/Data/ExteriorSignageQuoteService';

const useStyles = makeStyles(theme => ({
  borderDanger: {
    borderColor: `${theme.palette.error.main}`,
  },
  dropzoneNormal: {
    minHeight: '150px',
  },
  dropzoneCompact: {
    minHeight: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '10px',
    '& .MuiTypography-root': {
      fontSize: '0.9rem',
      margin: 0,
    },
  },
  galleryContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: theme.spacing(2),
    width: '100%',
    alignItems: 'stretch',
  },
  dropzoneFullWidth: {
    gridColumn: '1 / -1',
  },
  imageContainer: {
    position: 'relative',
    border: '1px solid #ddd',
    padding: '5px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    height: '80px',
  },
}));

const ExteriorSignageQuoteUploadFileSection = ({
  exteriorSignageQuote,
  itemId,
  isDisabled,
  fileType,
  maxFiles = 1,
  titleText,
  formatText,
  dropzoneText,
  hasError = false,
  isLegacyQuote = false,
}) => {
  const classes = useStyles();

  const { mutate: updateExteriorSignageQuoteFile } = useUpdateExteriorSignageQuoteFile();
  const { mutate: deleteExteriorSignageQuoteFile } = useDeleteExteriorSignageQuoteFile();

  const currentFiles = useMemo(() => {
    const files = exteriorSignageQuote?.files || [];

    if (isLegacyQuote && fileType === ExteriorSignageQuoteAttachedFileTypeEnum.Logo) {
      return files.filter(f => f.exteriorSignageQuoteItem_Id == null);
    }

    return files.filter(f => f.exteriorSignageQuoteType === fileType && f.exteriorSignageQuoteItem_Id === itemId);
  }, [exteriorSignageQuote?.files, fileType, itemId, isLegacyQuote]);

  const canUploadMore = currentFiles.length < maxFiles;

  const handleDropFileArea = files => {
    if (files.length > 0) {
      updateExteriorSignageQuoteFile({
        exteriorSignageQuoteId: exteriorSignageQuote.id,
        files: files,
        exteriorSignageQuoteType: fileType,
        exteriorSignageQuoteItemId: itemId,
      });
    }
  };

  const handleDeletePhoto = attachedFileId => {
    deleteExteriorSignageQuoteFile({ exteriorSignageQuoteId: exteriorSignageQuote.id, photoId: attachedFileId });
  };

  const useCompactDropzone = currentFiles.length > 0 && maxFiles > 1;

  return (
    <Box>
      <GridItem xs={12} style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
        <Typography variant="h5" sx={{ my: 1 }} color={hasError ? 'error' : 'initial'}>
          {titleText} {maxFiles > 0 ? `(${currentFiles.length}/${maxFiles})` : ''}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
          {formatText}
        </Typography>
      </GridItem>

      <GridItem xs={12}>
        <Box className={classes.galleryContainer}>
          {currentFiles.map(file => (
            <Box key={file.id} className={classes.imageContainer}>
              {!isDisabled && (
                <Fab
                  color="error"
                  size="small"
                  onClick={() => handleDeletePhoto(file.id)}
                  style={{
                    position: 'absolute',
                    top: 5,
                    right: 5,
                    zIndex: 10,
                    width: '28px',
                    height: '28px',
                    minHeight: '28px',
                  }}>
                  <Delete style={{ fontSize: '1.2rem' }} />
                </Fab>
              )}
              <img
                src={`${HOST}/api/dashboard/attached-files/${exteriorSignageQuote.id}/${file.id}/download?type=${BlobStorageTypeEnumValue.ExteriorSignageQuote}`}
                alt={file.fileName}
                style={{ height: '60px', width: '100%', objectFit: 'contain' }}
              />
            </Box>
          ))}

          {!isDisabled && canUploadMore && (
            <Box className={useCompactDropzone ? '' : classes.dropzoneFullWidth}>
              <DropzoneArea
                key={currentFiles.length}
                dropzoneClass={`
                  ${hasError ? classes.borderDanger : ''} 
                  ${useCompactDropzone ? classes.dropzoneCompact : classes.dropzoneNormal}
                `}
                maxFileSize={UPLOAD_MAX_IMAGE_SIZE}
                filesLimit={maxFiles - currentFiles.length}
                showPreviewsInDropzone={false}
                showAlerts={false}
                onChange={files => handleDropFileArea(files)}
                Icon={() => <></>}
                dropzoneText={useCompactDropzone ? '+' : dropzoneText}
              />
            </Box>
          )}
        </Box>
      </GridItem>
    </Box>
  );
};

export default ExteriorSignageQuoteUploadFileSection;
